import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { SectorBreakdown } from '../../types';
import { colors } from '../../styles/theme';
import { committedSections, PROPOSED_SECTION, SECTION_LABELS } from '../../utils/analyticsSections';
import styles from './PipelineBySector.module.css';

// The four committed sections keep their legacy hex (they predate the tokens-only rule and
// re-toning the whole page is not this change); the candidate segment reads the theme.
const SECTION_COLORS: Record<string, string> = {
  live: '#1A1A1A',
  screening: '#E65100',
  origination: '#1565C0',
  pending: '#999',
  [PROPOSED_SECTION]: colors.candidate.proposed,
};

interface Props {
  data: SectorBreakdown[];
  /** The page's "Include AI-proposed deals" lens. ON appends the candidate segment to each bar. */
  includeCandidates?: boolean;
}

export function PipelineBySector({ data, includeCandidates = false }: Props) {
  // The lens gates CATEGORIES as well as series. `total` is committed-only (it excludes `proposed`),
  // and the backend emits a sector row when `total > 0 OR proposed > 0` — so a sector reached only
  // by candidates arrives as a `total: 0` row. Drawing it with the lens off would add a bar, and a
  // row of chart height, to a card whose meaning is supposed to be unchanged.
  const rows = includeCandidates ? data : data.filter((d) => d.total > 0);

  if (rows.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.headerBar}>
          <span className={styles.headerLabel}>Pipeline by Sector</span>
        </div>
        <div className={styles.empty}>No sector data available</div>
      </div>
    );
  }

  const chartData = rows.map((d) => ({
    ...d,
    // Defaulted here, not on the <Bar>: a cached pre-change payload has no `proposed` field at all,
    // and the tooltip would otherwise print a blank value beside the "Candidate" key.
    proposed: d.proposed ?? 0,
    name: d.sector,
  }));

  // A1: draw a status group only when the data has one. The candidate segment is the page toggle's,
  // and always lands LAST in the stack so the committed bar keeps the length it has today.
  const sections: string[] = committedSections(rows);
  if (includeCandidates) sections.push(PROPOSED_SECTION);

  return (
    <div className={styles.card}>
      <div className={styles.headerBar}>
        <span className={styles.headerLabel}>Pipeline by Sector</span>
      </div>
      <div className={styles.body}>
        <ResponsiveContainer width="100%" height={Math.max(200, rows.length * 36)}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
          >
            <XAxis
              type="number"
              tick={{ fontSize: 10, fontFamily: "'DM Mono', monospace", fill: '#999' }}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 12, fontFamily: "'DM Sans', sans-serif", fill: '#1A1A1A' }}
              width={280}
            />
            <Tooltip
              contentStyle={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid #E5E5E0',
              }}
            />
            <Legend
              wrapperStyle={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                textTransform: 'uppercase',
              }}
            />
            {sections.map((section, i) => (
              <Bar
                key={section}
                dataKey={section}
                name={SECTION_LABELS[section]}
                stackId="a"
                fill={SECTION_COLORS[section]}
                radius={i === sections.length - 1 ? [0, 3, 3, 0] : undefined}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
